import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Honeybadger from '@honeybadger-io/js';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';

import { useTableContext } from 'lib/context/TableContext/TableContext';
import numOrDefault from 'lib/helpers/numOrDefault';
import { errorMessageGuard, iaRa } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import CurrencyInput from 'components/shared/CurrencyInput/CurrencyInput';
import { useComment } from 'components/shared/ExpandableToolbar/components/ActivityLog/useComment';
import Confirm from 'components/shared/Modal/Confirm/Confirm';
import Popover from 'components/shared/Popovers/Popover/Popover';
import ReceiptModal from 'components/shared/Receipt/ReceiptModal';
import Switch from 'components/shared/Switch/Switch';
import Textarea from 'components/shared/Textarea/Textarea';

export const Actions = ({ data }) => {
  const { id, status, amount_paid_self, patient_id, practice_id, surcharge_amount, isPatient } =
    data || {};

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { setForceRefresh } = useTableContext();

  const [showReceipt, setShowReceipt] = useState(false);
  const [refundTransactionModal, setRefundTransactionModal] = useState(false);
  const [includeSurcharge, setIncludeSurcharge] = useState(true);
  const [refundAmount, setRefundAmount] = useState(amount_paid_self);
  const [noteValue, setNoteValue] = useState('');

  const { onCommentPostHandler } = useComment('transaction', id);

  useEffect(() => {
    const parsedRefundAmount = numOrDefault(refundAmount);
    const parsedSurchargeAmount = numOrDefault(surcharge_amount);
    let amount = 1;

    if (includeSurcharge) {
      const withSurcharge = parsedRefundAmount + parsedSurchargeAmount;

      if (withSurcharge > amount_paid_self) amount = amount_paid_self;
      else amount = withSurcharge;
    } else {
      const withoutSurcharge = parsedRefundAmount - parsedSurchargeAmount;

      if (withoutSurcharge < 1) amount = 1;
      else amount = withoutSurcharge;
    }

    setRefundAmount(amount);
  }, [includeSurcharge, surcharge_amount]);

  const onRefund = async () => {
    try {
      const res = await requestApi({
        navigate,
        url: '/api/transactions/online/refund',
        params: {
          transactionId: id,
          amount: Number(refundAmount),
          includeSurcharge
        }
      });

      if (noteValue) {
        await onCommentPostHandler(noteValue);
      }
      const { code, error, transaction, patient_balance } = res;

      switch (code) {
        case 0:
          showAlert({ title: 'Successfully refunded transaction!', color: 'success' });

          // refetch activities
          //we need timeout due to saving comment delay
          setTimeout(() => {
            queryClient.refetchQueries({ queryKey: ['activities', 'transaction', id] });
          }, 0);

          queryClient.setQueriesData({ queryKey: ['transactions'], type: 'active' }, (oldData) => {
            try {
              const newTransactionsData = oldData.transactions.map((v) => {
                if (v.practice_id !== practice_id || v?.id !== id) {
                  return v;
                }
                const { status, amount_refunded, amount_paid_self } = transaction;
                return {
                  ...v,
                  status,
                  amount_refunded,
                  amount_paid_self
                };
              });
              const updatedStatusPanel = iaRa(oldData?.statusPanelData).map((data) => {
                if (data.field === 'amount_paid_self') {
                  return { ...data, value: data.value - (refundAmount || 0) };
                }
                return data;
              });

              return {
                ...oldData,
                statusPanelData: updatedStatusPanel,
                transactions: newTransactionsData
              };
            } catch (e) {
              console.error('Error updating transaction data:', e);
              return oldData;
            }
          });
          setForceRefresh((prev) => !prev);
          queryClient.setQueriesData(['patientBalance', patient_id], (oldData) => {
            try {
              return { ...oldData, patientBalance: patient_balance };
            } catch (e) {
              console.error(e);
            }
          });
          break;
        case 6:
          break;
        case 7:
          showAlert({ title: 'Successfully voided transaction!', color: 'success' });
          break;
        default:
          if (error) {
            showAlert({
              title: errorMessageGuard(error),
              color: 'danger'
            });
            Honeybadger.notify(`handleTransactionRefund: ${error}`);
          } else {
            Honeybadger.notify('handleTransactionRefund: unexpected error');
          }
          break;
      }
    } catch (error) {
      console.error(error);
      Honeybadger.notify(`handleTransactionRefund: unexpected error ${error}`);
    }
  };

  const actionsOptions = [
    {
      label: 'View receipt',
      icon: 'eye-mono',
      onClick: () => setShowReceipt(true)
    },
    ...(['completed', 'partially_refunded'].includes(status?.toLowerCase()) && !isPatient
      ? [
          {
            label: 'Refund',
            icon: 'money-send-mono',
            onClick: () => setRefundTransactionModal(true)
          }
        ]
      : [])
  ];

  return (
    <>
      <Popover
        className="flex h-full w-full items-center justify-center"
        position="left"
        isFixed
        icon="context-menu-horizontal"
        options={actionsOptions}
        isBorder={false}
      />
      {showReceipt && (
        <ReceiptModal
          transactionId={id}
          isOpen={showReceipt}
          onClose={() => setShowReceipt(false)}
        />
      )}
      {!!refundTransactionModal && (
        <Confirm
          icon="info-filled"
          iconSize={42}
          variant="warning"
          primaryBtnTxt="Refund"
          title="Refund Transaction"
          message="Are you sure you want to refund this Transaction?"
          handleContinue={() => {
            onRefund();
            setRefundTransactionModal(false);
          }}
          handleOpen={!!refundTransactionModal}
          handleClose={() => setRefundTransactionModal(false)}>
          <div className="mt-4">
            <CurrencyInput
              id="refund_amount"
              label="Refund Amount"
              value={refundAmount}
              className="!h-10 !rounded-md border border-neutral-100"
              onValueChange={(v) => setRefundAmount(v)}
              maxValue={
                includeSurcharge && surcharge_amount
                  ? amount_paid_self
                  : amount_paid_self - numOrDefault(surcharge_amount)
              }
              minValue={1}
            />

            <Textarea
              label="Refund note"
              placeholder="Add any note here"
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
            />

            {surcharge_amount ? (
              <div className="w-100 mt-[12px] flex items-center justify-between">
                <label className="mr-3">Refund Technology Fee?</label>
                <Switch
                  onChange={() => setIncludeSurcharge((prev) => !prev)}
                  checked={includeSurcharge}
                />
              </div>
            ) : null}
          </div>
        </Confirm>
      )}
    </>
  );
};

export default Actions;
